import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  Upload, CheckCircle2, AlertTriangle, XCircle, Clock, Banknote,
  ArrowDownCircle, ArrowUpCircle, FileText, Trash2, Link2, Link2Off,
  Search, Calendar, Download, BookOpen
} from 'lucide-react';
import AdminLayout from '@/components/admin/AdminLayout';
import { useTranslation } from 'react-i18next';

const API = process.env.REACT_APP_BACKEND_URL;

const BankReconciliationPage = ({ auth, onLogout }) => {
  const { t } = useTranslation();
  const [tab, setTab] = useState('dashboard');

  return (
    <AdminLayout auth={auth} onLogout={onLogout}>
      <div className="space-y-5 max-w-[1200px] mx-auto" data-testid="bank-reconciliation-page">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-white">Rapprochement bancaire</h1>
          <p className="text-sm text-slate-500 mt-0.5">Import CAMT, suivi des paiements et journal des encaissements</p>
        </div>

        <div className="flex bg-slate-100 dark:bg-slate-800 rounded-xl p-1 gap-1" data-testid="bank-tabs">
          {[
            { key: 'dashboard', label: 'Tableau de bord', icon: Banknote },
            { key: 'import', label: 'Import CAMT', icon: Upload },
            { key: 'transactions', label: 'Transactions', icon: ArrowDownCircle },
            { key: 'journal', label: 'Journal', icon: BookOpen },
          ].map(t => (
            <button key={t.key} onClick={() => setTab(t.key)}
              className={`flex-1 py-2.5 px-3 text-sm font-medium rounded-lg transition-all flex items-center justify-center gap-1.5 ${tab === t.key ? 'bg-white dark:bg-slate-700 text-slate-900 dark:text-white shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
              data-testid={`tab-${t.key}`}>
              <t.icon className="w-4 h-4" /> {t.label}
            </button>
          ))}
        </div>

        {tab === 'dashboard' && <DashboardTab />}
        {tab === 'import' && <ImportTab onImported={() => setTab('transactions')} />}
        {tab === 'transactions' && <TransactionsTab />}
        {tab === 'journal' && <JournalTab />}
      </div>
    </AdminLayout>
  );
};

/* ═══════════ DASHBOARD TAB ═══════════ */
const DashboardTab = () => {
  const { t } = useTranslation();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API}/api/bank/dashboard`);
        setData(res.data);
      } catch { toast.error("Erreur chargement"); }
      finally { setLoading(false); }
    })();
  }, []);

  if (loading) return <div className="text-center py-16 text-slate-400">{t('common.loading')}</div>;
  if (!data) return null;

  const stats = [
    { label: 'Total facture', value: `CHF ${data.total_billed.toLocaleString('fr-CH', { minimumFractionDigits: 2 })}`, icon: FileText, color: 'bg-slate-100 text-slate-700' },
    { label: 'Encaisse', value: `CHF ${data.total_paid.toLocaleString('fr-CH', { minimumFractionDigits: 2 })}`, icon: CheckCircle2, color: 'bg-emerald-50 text-emerald-700' },
    { label: 'En attente', value: `CHF ${data.total_outstanding.toLocaleString('fr-CH', { minimumFractionDigits: 2 })}`, icon: Clock, color: 'bg-amber-50 text-amber-700' },
  ];

  const counters = [
    { label: 'Payees', count: data.paid_count, color: 'bg-emerald-500' },
    { label: 'Partielles', count: data.partial_count, color: 'bg-amber-500' },
    { label: 'Impayees', count: data.unpaid_count, color: 'bg-red-500' },
    { label: 'En retard', count: data.overdue_count, color: 'bg-rose-600' },
  ];

  return (
    <div className="space-y-5" data-testid="dashboard-tab">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {stats.map((s, i) => (
          <Card key={i} className="p-4">
            <div className="flex items-center gap-3">
              <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${s.color}`}>
                <s.icon className="w-5 h-5" />
              </div>
              <div>
                <p className="text-xs text-slate-500 font-medium">{s.label}</p>
                <p className="text-lg font-bold text-slate-900 dark:text-white" data-testid={`stat-${i}`}>{s.value}</p>
              </div>
            </div>
          </Card>
        ))}
      </div>

      <div className="flex gap-3">
        {counters.map((c, i) => (
          <Card key={i} className="flex-1 p-3 text-center">
            <div className={`w-8 h-8 rounded-full ${c.color} text-white mx-auto flex items-center justify-center font-bold text-sm mb-1`} data-testid={`counter-${c.label.toLowerCase()}`}>
              {c.count}
            </div>
            <p className="text-xs text-slate-500 font-medium">{c.label}</p>
          </Card>
        ))}
      </div>

      {data.invoices.length > 0 && (
        <Card className="overflow-hidden">
          <div className="p-3 border-b bg-slate-50 dark:bg-slate-800">
            <p className="text-sm font-semibold text-slate-700 dark:text-slate-200">Factures</p>
          </div>
          <div className="divide-y max-h-96 overflow-y-auto">
            {data.invoices.filter(i => i.status !== 'annulee').map(inv => (
              <div key={inv.id} className="flex items-center justify-between px-4 py-2.5 hover:bg-slate-50 transition-colors" data-testid={`inv-row-${inv.id}`}>
                <div className="flex items-center gap-3 min-w-0">
                  <StatusIcon status={inv.status} />
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-slate-800">{inv.number}</p>
                    <p className="text-xs text-slate-500 truncate">{inv.client_name}</p>
                  </div>
                </div>
                <div className="text-right shrink-0">
                  <p className="text-sm font-semibold text-slate-800">CHF {(inv.total || 0).toFixed(2)}</p>
                  <div className="flex items-center gap-1.5">
                    {inv.paid_amount > 0 && <span className="text-xs text-emerald-600">Recu: {inv.paid_amount.toFixed(2)}</span>}
                    <StatusBadge status={inv.status} />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </Card>
      )}
    </div>
  );
};

/* ═══════════ IMPORT TAB ═══════════ */
const ImportTab = ({ onImported }) => {
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);
  const [statements, setStatements] = useState([]);

  const loadStatements = useCallback(async () => {
    try { const res = await axios.get(`${API}/api/bank/statements`); setStatements(res.data); } catch {}
  }, []);

  useEffect(() => { loadStatements(); }, [loadStatements]);

  const handleUpload = async () => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await axios.post(`${API}/api/bank/upload-camt`, fd);
      setResult(res.data);
      toast.success(`${res.data.matched} transaction(s) rapprochee(s) automatiquement`);
      loadStatements();
      setFile(null);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur lors de l'import");
    } finally { setUploading(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Supprimer ce releve et toutes ses transactions ?")) return;
    try {
      await axios.delete(`${API}/api/bank/statements/${id}`);
      toast.success("Releve supprime");
      loadStatements();
    } catch { toast.error("Erreur suppression"); }
  };

  return (
    <div className="space-y-5" data-testid="import-tab">
      <Card className="p-6">
        <div className="text-center mb-5">
          <Upload className="w-12 h-12 text-slate-300 mx-auto mb-3" />
          <h3 className="font-semibold text-slate-800 mb-1">Import fichier CAMT</h3>
          <p className="text-sm text-slate-500">Formats acceptes : CAMT.053 (extrait), CAMT.054 (avis de paiement)</p>
        </div>
        <div className="flex items-center gap-3 max-w-md mx-auto">
          <Input type="file" accept=".xml" onChange={e => { setFile(e.target.files[0]); setResult(null); }}
            className="flex-1" data-testid="camt-file-input" />
          <Button onClick={handleUpload} disabled={!file || uploading}
            className="bg-blue-600 hover:bg-blue-700 text-white shrink-0" data-testid="upload-btn">
            {uploading ? 'Import...' : 'Importer'}
          </Button>
        </div>
      </Card>

      {result && (
        <Card className="p-5 border-emerald-200 bg-emerald-50/50" data-testid="import-result">
          <h4 className="font-semibold text-emerald-800 mb-3">Resultat de l'import</h4>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
            <div><span className="text-slate-500">Format :</span> <strong>{result.format}</strong></div>
            <div><span className="text-slate-500">Transactions :</span> <strong>{result.total_transactions}</strong></div>
            <div><span className="text-slate-500">Credits :</span> <strong className="text-emerald-600">{result.credits}</strong></div>
            <div><span className="text-slate-500">Debits :</span> <strong className="text-red-600">{result.debits}</strong></div>
            <div><span className="text-slate-500">Rapproches :</span> <strong className="text-emerald-600">{result.matched}</strong></div>
            <div><span className="text-slate-500">Non rapproches :</span> <strong className="text-amber-600">{result.unmatched}</strong></div>
          </div>
          {result.unmatched > 0 && (
            <Button size="sm" variant="outline" className="mt-3" onClick={onImported} data-testid="view-unmatched-btn">
              Voir les transactions non rapprochees
            </Button>
          )}
        </Card>
      )}

      {statements.length > 0 && (
        <Card className="overflow-hidden">
          <div className="p-3 border-b bg-slate-50">
            <p className="text-sm font-semibold text-slate-700">Releves importes</p>
          </div>
          <div className="divide-y">
            {statements.map(s => (
              <div key={s.id} className="flex items-center justify-between px-4 py-3" data-testid={`stmt-${s.id}`}>
                <div>
                  <p className="text-sm font-medium text-slate-800">{s.filename}</p>
                  <p className="text-xs text-slate-500">
                    {s.format} | {s.total_credits} credits, {s.total_debits} debits | CHF {s.credit_amount?.toFixed(2)} entrant
                  </p>
                  <p className="text-xs text-slate-400">
                    Importe le {new Date(s.uploaded_at).toLocaleDateString('fr-CH', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                  </p>
                </div>
                <Button size="sm" variant="ghost" className="text-red-500 hover:text-red-700"
                  onClick={() => handleDelete(s.id)} data-testid={`delete-stmt-${s.id}`}>
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            ))}
          </div>
        </Card>
      )}
    </div>
  );
};

/* ═══════════ TRANSACTIONS TAB ═══════════ */
const TransactionsTab = () => {
  const { t } = useTranslation();
  const [txns, setTxns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [matchDialog, setMatchDialog] = useState(null);
  const [invoices, setInvoices] = useState([]);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const q = filter !== 'all' ? `?status=${filter}` : '';
      const [txRes, invRes] = await Promise.all([
        axios.get(`${API}/api/bank/transactions${q}`),
        axios.get(`${API}/api/bank/unmatched-invoices`),
      ]);
      setTxns(txRes.data);
      setInvoices(invRes.data);
    } catch { toast.error("Erreur chargement"); }
    finally { setLoading(false); }
  }, [filter]);

  useEffect(() => { loadData(); }, [loadData]);

  const handleMatch = async (txnId, invoiceId) => {
    try {
      await axios.post(`${API}/api/bank/transactions/${txnId}/match`, { invoice_id: invoiceId });
      toast.success("Transaction rapprochee");
      setMatchDialog(null);
      loadData();
    } catch (err) { toast.error(err.response?.data?.detail || "Erreur"); }
  };

  const handleUnmatch = async (txnId) => {
    try {
      await axios.post(`${API}/api/bank/transactions/${txnId}/unmatch`);
      toast.success("Rapprochement annule");
      loadData();
    } catch (err) { toast.error(err.response?.data?.detail || "Erreur"); }
  };

  const filtered = txns.filter(t => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (t.debtor_name || '').toLowerCase().includes(q)
      || (t.creditor_name || '').toLowerCase().includes(q)
      || (t.matched_invoice_number || '').toLowerCase().includes(q)
      || (t.unstructured_info || '').toLowerCase().includes(q)
      || (t.reference || '').toLowerCase().includes(q);
  });

  return (
    <div className="space-y-4" data-testid="transactions-tab">
      <div className="flex items-center gap-3 flex-wrap">
        <div className="flex gap-1.5">
          {[
            { v: 'all', l: 'Toutes' },
            { v: 'matched', l: 'Rapprochees', c: 'bg-emerald-600 text-white' },
            { v: 'unmatched', l: 'Non rapprochees', c: 'bg-amber-600 text-white' },
            { v: 'debit', l: 'Debits', c: 'bg-red-600 text-white' },
          ].map(f => (
            <Button key={f.v} size="sm" variant={filter === f.v ? "default" : "outline"}
              onClick={() => setFilter(f.v)}
              className={filter === f.v ? (f.c || "") : ""}
              data-testid={`filter-${f.v}`}>
              {f.l}
            </Button>
          ))}
        </div>
        <div className="relative flex-1 max-w-xs">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Rechercher..."
            className="pl-9" data-testid="txn-search" />
        </div>
        <span className="text-sm text-slate-500 ml-auto">{filtered.length} transaction(s)</span>
      </div>

      {loading ? (
        <div className="text-center py-12 text-slate-400">{t('common.loading')}</div>
      ) : filtered.length === 0 ? (
        <Card className="p-12 text-center">
          <ArrowDownCircle className="w-10 h-10 text-slate-300 mx-auto mb-3" />
          <p className="text-sm font-medium text-slate-600">Aucune transaction</p>
          <p className="text-xs text-slate-400 mt-1">Importez un fichier CAMT pour commencer</p>
        </Card>
      ) : (
        <div className="space-y-2">
          {filtered.map(txn => (
            <Card key={txn.id} className={`p-3.5 transition-shadow hover:shadow-md ${txn.status === 'matched' ? 'border-l-4 border-l-emerald-400' : txn.status === 'unmatched' ? 'border-l-4 border-l-amber-400' : 'border-l-4 border-l-slate-300'}`}
              data-testid={`txn-${txn.id}`}>
              <div className="flex items-center justify-between gap-3">
                <div className="flex items-center gap-3 min-w-0">
                  <div className={`w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0 ${txn.credit_debit === 'CRDT' ? 'bg-emerald-100' : 'bg-red-100'}`}>
                    {txn.credit_debit === 'CRDT' ? <ArrowDownCircle className="w-4 h-4 text-emerald-600" /> : <ArrowUpCircle className="w-4 h-4 text-red-600" />}
                  </div>
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="text-sm font-semibold text-slate-800">
                        {txn.credit_debit === 'CRDT' ? txn.debtor_name || 'Crediteur inconnu' : txn.creditor_name || 'Debiteur inconnu'}
                      </p>
                      {txn.matched_invoice_number && (
                        <Badge className="bg-emerald-100 text-emerald-700 text-xs">{txn.matched_invoice_number}</Badge>
                      )}
                      {txn.match_method && (
                        <span className="text-xs text-slate-400">({txn.match_method})</span>
                      )}
                    </div>
                    <p className="text-xs text-slate-500 mt-0.5">
                      {txn.booking_date} {txn.reference && `| Ref: ${txn.reference}`}
                    </p>
                    {txn.unstructured_info && <p className="text-xs text-slate-400 truncate max-w-md">{txn.unstructured_info}</p>}
                  </div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <p className={`text-sm font-bold ${txn.credit_debit === 'CRDT' ? 'text-emerald-700' : 'text-red-700'}`}>
                    {txn.credit_debit === 'CRDT' ? '+' : '-'} CHF {txn.amount.toFixed(2)}
                  </p>
                  {txn.status === 'unmatched' && (
                    <Button size="sm" variant="outline" className="text-blue-600 border-blue-200"
                      onClick={() => setMatchDialog(txn)} data-testid={`match-btn-${txn.id}`}>
                      <Link2 className="w-3.5 h-3.5 mr-1" /> Rapprocher
                    </Button>
                  )}
                  {txn.status === 'matched' && (
                    <Button size="sm" variant="ghost" className="text-slate-400 hover:text-red-600"
                      onClick={() => handleUnmatch(txn.id)} data-testid={`unmatch-btn-${txn.id}`}>
                      <Link2Off className="w-3.5 h-3.5" />
                    </Button>
                  )}
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}

      {/* Manual match dialog */}
      <Dialog open={!!matchDialog} onOpenChange={() => setMatchDialog(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Rapprocher la transaction</DialogTitle>
          </DialogHeader>
          {matchDialog && (
            <div className="space-y-4">
              <div className="p-3 bg-blue-50 rounded-lg text-sm">
                <p><strong>{matchDialog.debtor_name || 'Inconnu'}</strong></p>
                <p className="text-emerald-700 font-bold">+ CHF {matchDialog.amount.toFixed(2)}</p>
                <p className="text-xs text-slate-500 mt-1">{matchDialog.booking_date}</p>
              </div>
              <div>
                <Label className="text-sm font-medium mb-2 block">Selectionner la facture</Label>
                {invoices.length === 0 ? (
                  <p className="text-sm text-slate-400">Aucune facture en attente</p>
                ) : (
                  <div className="max-h-64 overflow-y-auto space-y-1.5">
                    {invoices.map(inv => (
                      <button key={inv.id}
                        onClick={() => handleMatch(matchDialog.id, inv.id)}
                        className="w-full p-2.5 text-left rounded-lg border hover:bg-blue-50 hover:border-blue-300 transition-colors"
                        data-testid={`match-inv-${inv.id}`}>
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-sm font-medium text-slate-800">{inv.number}</p>
                            <p className="text-xs text-slate-500">{inv.client_name}</p>
                          </div>
                          <div className="text-right">
                            <p className="text-sm font-bold text-slate-700">CHF {(inv.total || 0).toFixed(2)}</p>
                            {(inv.paid_amount || 0) > 0 && (
                              <p className="text-xs text-amber-600">Deja recu: {inv.paid_amount.toFixed(2)}</p>
                            )}
                          </div>
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

/* ═══════════ JOURNAL TAB ═══════════ */
const JournalTab = () => {
  const { t } = useTranslation();
  const [journal, setJournal] = useState({ payments: [], total: 0, count: 0 });
  const [loading, setLoading] = useState(true);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  const loadJournal = useCallback(async () => {
    setLoading(true);
    try {
      let q = '';
      const params = [];
      if (dateFrom) params.push(`date_from=${dateFrom}`);
      if (dateTo) params.push(`date_to=${dateTo}`);
      if (params.length) q = '?' + params.join('&');
      const res = await axios.get(`${API}/api/bank/journal${q}`);
      setJournal(res.data);
    } catch { toast.error("Erreur chargement"); }
    finally { setLoading(false); }
  }, [dateFrom, dateTo]);

  useEffect(() => { loadJournal(); }, [loadJournal]);

  const exportCSV = () => {
    const rows = [['Date', 'N° Facture', 'Client', 'Vehicule', 'Montant recu', 'Total facture', 'Methode']];
    journal.payments.forEach(p => {
      rows.push([p.date, p.invoice_number, p.client_name, p.vehicle_info || '', p.amount.toFixed(2), (p.invoice_total || 0).toFixed(2), p.match_method || '']);
    });
    rows.push(['', '', '', '', '', '', '']);
    rows.push(['TOTAL', '', '', '', journal.total.toFixed(2), '', '']);
    const csv = rows.map(r => r.map(c => `"${c}"`).join(';')).join('\n');
    const blob = new Blob(['\ufeff' + csv], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `journal_encaissements_${dateFrom || 'all'}_${dateTo || 'all'}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-4" data-testid="journal-tab">
      <div className="flex items-center gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <Calendar className="w-4 h-4 text-slate-400" />
          <Input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)}
            className="w-40" data-testid="date-from" />
          <span className="text-slate-400">—</span>
          <Input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)}
            className="w-40" data-testid="date-to" />
        </div>
        <Button size="sm" variant="outline" onClick={exportCSV} className="ml-auto" data-testid="export-csv-btn">
          <Download className="w-4 h-4 mr-1" /> Export CSV
        </Button>
      </div>

      <Card className="p-4 bg-blue-50/50 border-blue-200">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-xs text-blue-600 font-medium">Total encaissements</p>
            <p className="text-xl font-bold text-blue-800" data-testid="journal-total">
              CHF {journal.total.toLocaleString('fr-CH', { minimumFractionDigits: 2 })}
            </p>
          </div>
          <p className="text-sm text-blue-600">{journal.count} paiement(s)</p>
        </div>
      </Card>

      {loading ? (
        <div className="text-center py-12 text-slate-400">Chargement...</div>
      ) : journal.payments.length === 0 ? (
        <Card className="p-12 text-center">
          <BookOpen className="w-10 h-10 text-slate-300 mx-auto mb-3" />
          <p className="text-sm font-medium text-slate-600">Aucun encaissement enregistre</p>
          <p className="text-xs text-slate-400 mt-1">Les paiements apparaitront ici apres l'import CAMT</p>
        </Card>
      ) : (
        <Card className="overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-slate-50 dark:bg-slate-800 text-left">
                <th className="py-2.5 px-4 font-semibold text-slate-600">Date</th>
                <th className="py-2.5 px-4 font-semibold text-slate-600">Facture</th>
                <th className="py-2.5 px-4 font-semibold text-slate-600">Client</th>
                <th className="py-2.5 px-4 font-semibold text-slate-600">Vehicule</th>
                <th className="py-2.5 px-4 font-semibold text-slate-600 text-right">Recu</th>
                <th className="py-2.5 px-4 font-semibold text-slate-600 text-right">Total facture</th>
                <th className="py-2.5 px-4 font-semibold text-slate-600">Methode</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {journal.payments.map(p => (
                <tr key={p.id} className="hover:bg-slate-50 transition-colors" data-testid={`journal-row-${p.id}`}>
                  <td className="py-2 px-4 text-slate-700">{p.date}</td>
                  <td className="py-2 px-4 font-medium text-slate-800">{p.invoice_number}</td>
                  <td className="py-2 px-4 text-slate-700">{p.client_name}</td>
                  <td className="py-2 px-4 text-slate-500 text-xs">{p.vehicle_info || '-'}</td>
                  <td className="py-2 px-4 text-right font-semibold text-emerald-700">CHF {p.amount.toFixed(2)}</td>
                  <td className="py-2 px-4 text-right text-slate-600">CHF {(p.invoice_total || 0).toFixed(2)}</td>
                  <td className="py-2 px-4">
                    <Badge className={p.match_method === 'manual' ? 'bg-blue-100 text-blue-700' : 'bg-emerald-100 text-emerald-700'}>
                      {p.match_method || 'auto'}
                    </Badge>
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="bg-slate-50 font-bold">
                <td colSpan={4} className="py-2.5 px-4 text-slate-700">Total</td>
                <td className="py-2.5 px-4 text-right text-emerald-700">CHF {journal.total.toFixed(2)}</td>
                <td colSpan={2}></td>
              </tr>
            </tfoot>
          </table>
        </Card>
      )}
    </div>
  );
};

/* ═══════════ SHARED COMPONENTS ═══════════ */
const StatusIcon = ({ status }) => {
  if (status === 'payee') return <CheckCircle2 className="w-4 h-4 text-emerald-500 shrink-0" />;
  if (status === 'partielle') return <AlertTriangle className="w-4 h-4 text-amber-500 shrink-0" />;
  return <Clock className="w-4 h-4 text-slate-400 shrink-0" />;
};

const StatusBadge = ({ status }) => {
  const map = {
    payee: 'bg-emerald-100 text-emerald-700',
    partielle: 'bg-amber-100 text-amber-700',
    envoyee: 'bg-blue-100 text-blue-700',
    brouillon: 'bg-slate-100 text-slate-600',
  };
  const labels = { payee: 'Payee', partielle: 'Partielle', envoyee: 'En attente', brouillon: 'Brouillon' };
  return <Badge className={`text-xs ${map[status] || map.brouillon}`}>{labels[status] || status}</Badge>;
};

export default BankReconciliationPage;
